import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MessageCircle, Trash2, Loader2, Sparkles, Search, X, Bot, Pin } from 'lucide-react';
import { fallbackAvatarUrl } from '../utils/fallbackAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Chats() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      const timer = setTimeout(() => searchMessages(), 400);
      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const fetchConversations = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/conversations`, { withCredentials: true });
      setConversations(data);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const searchMessages = async () => {
    setSearching(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/chat/search?q=${encodeURIComponent(searchQuery)}`, { withCredentials: true });
      setSearchResults(data);
    } catch (err) { console.error('Search error:', err); }
    finally { setSearching(false); }
  };

  const deleteConversation = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await axios.delete(`${API_URL}/api/conversations/${id}`, { withCredentials: true });
      setConversations(prev => prev.filter(c => c.id !== id));
    } catch {}
  };

  const defaultAvatar = fallbackAvatarUrl(''); // generic — used in search results; per-character fallback applied inline below

  return (
    <div className="min-h-screen pb-20 lg:pb-12">
      <div className="max-w-4xl mx-auto px-0 lg:px-6">
      {/* Header */}
      <div className="px-4 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white font-['Unbounded']">Messages</h1>
            <p className="text-sm text-[#71717A] mt-1">Continue your conversations</p>
          </div>
          <button
            onClick={() => { setShowSearch(!showSearch); setSearchQuery(''); setSearchResults([]); }}
            className={`p-2.5 rounded-full transition-colors ${showSearch ? 'bg-[#E11D48] text-white' : 'bg-[#1A1D24] text-[#A1A1AA] hover:text-white'}`}
            data-testid="search-toggle-btn"
          >
            {showSearch ? <X className="w-5 h-5" /> : <Search className="w-5 h-5" />}
          </button>
        </div>

        {/* Search Bar */}
        {showSearch && (
          <div className="mt-3 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search your chat history..."
              className="pl-10 bg-[#1A1D24] border-white/10 text-white placeholder:text-[#71717A] rounded-xl"
              autoFocus
              data-testid="chat-search-input"
            />
            {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#E11D48] animate-spin" />}
          </div>
        )}
      </div>

      {/* Search Results */}
      {showSearch && searchQuery.length >= 2 && (
        <div className="px-4 mb-4">
          {searchResults.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs text-[#71717A] mb-2">{searchResults.length} results for "{searchQuery}"</p>
              {searchResults.map((result, i) => (
                <Link key={i} to={`/chat/${result.conversation_id}`} className="block" data-testid={`search-result-${i}`}>
                  <div className="flex items-start gap-3 p-3 bg-[#0F1115] border border-white/5 rounded-xl hover:bg-[#1A1D24] transition-colors">
                    <img src={result.character_avatar || fallbackAvatarUrl(result.character_name)} alt="" className="w-10 h-10 rounded-full object-cover border border-white/10" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-bold text-white">{result.character_name}</span>
                        <span className="text-[10px] text-[#71717A] px-1.5 py-0.5 bg-white/5 rounded-full">{result.role}</span>
                      </div>
                      <p className="text-xs text-[#A1A1AA] line-clamp-2">{result.content}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : !searching && (
            <p className="text-sm text-[#71717A] text-center py-4">No results found</p>
          )}
        </div>
      )}

      {/* Conversations List */}
      {!showSearch && (
        <>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-[#E11D48] animate-spin" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-16 px-4">
              <div className="w-20 h-20 rounded-full bg-[#1A1D24] flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-10 h-10 text-[#71717A]" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">No conversations yet</h3>
              <p className="text-sm text-[#71717A] mb-6">Start chatting with AI characters</p>
              <Link to="/">
                <Button className="bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-full" data-testid="browse-characters-button">
                  <Sparkles className="w-4 h-4 mr-2" /> Browse Characters
                </Button>
              </Link>
            </div>
          ) : (
            <div className="px-4 space-y-2">
              {conversations.map((conversation) => {
                const isBot = conversation.is_bot;
                const isUnread = isBot && conversation.unread;
                return (
                <Link key={conversation.id} to={`/chat/${conversation.id}`} className="block" data-testid={`conversation-${conversation.id}`}>
                  <div className={`relative flex items-center gap-3 p-3 rounded-xl transition-all group ${
                    isBot
                      ? 'bg-gradient-to-r from-[#E11D48]/10 via-fuchsia-500/5 to-transparent border border-[#E11D48]/25 hover:from-[#E11D48]/20 hover:border-[#E11D48]/40'
                      : 'bg-[#0F1115] border border-white/5 hover:bg-[#1A1D24]'
                  }`}>
                    {isBot && (
                      <Pin className="absolute top-2 right-2 w-3 h-3 text-[#E11D48]/70 rotate-45" />
                    )}
                    <div className="relative">
                      <img
                        src={conversation.character_avatar || fallbackAvatarUrl(conversation.character_name)}
                        alt={conversation.character_name}
                        className={`w-14 h-14 rounded-full object-cover border-2 ${isBot ? 'border-[#E11D48]/50' : 'border-white/10'}`}
                        onError={(e) => { e.target.src = fallbackAvatarUrl(conversation.character_name); }}
                      />
                      {isBot ? (
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#E11D48] border-2 border-[#0F1115] flex items-center justify-center" data-testid={`bot-badge-${conversation.id}`}>
                          <Bot className="w-3 h-3 text-white" />
                        </div>
                      ) : (
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-[#0F1115]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <h3 className={`font-bold text-sm truncate ${isUnread ? 'text-white' : 'text-white'}`}>{conversation.character_name}</h3>
                          {isBot && (
                            <span className="flex-shrink-0 text-[9px] font-black px-1.5 py-0.5 rounded-full bg-[#E11D48] text-white tracking-wider">BOT</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {isUnread && (
                            <span className="w-2 h-2 rounded-full bg-[#E11D48] animate-pulse" data-testid={`unread-dot-${conversation.id}`} />
                          )}
                          <span className="text-[10px] text-[#71717A]">{new Date(conversation.updated_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <p className={`text-xs truncate mt-1 ${isUnread ? 'text-white font-semibold' : 'text-[#A1A1AA]'}`}>
                        {conversation.last_message || 'Start chatting...'}
                      </p>
                      <span className="text-[10px] text-[#71717A] mt-1 block">{conversation.message_count} messages</span>
                    </div>
                    {!isBot && (
                      <button onClick={(e) => deleteConversation(conversation.id, e)} className="opacity-0 group-hover:opacity-100 p-2 text-[#71717A] hover:text-[#E11D48] transition-all" data-testid={`delete-conversation-${conversation.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </Link>
              );})}
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}
